import { Fragment } from "react";

import { Menu, Popover, Transition } from "@headlessui/react";
import { Bars3Icon, UserCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";

import Logo from "../assets/images/logo-acna.svg";
import { deleteSessionStorageFor } from "../lib/http/cookies";
import type { RootState } from "../redux/store";
import { logOutUser } from "../redux/userSlice";

const userNavigation = [
	{ name: "Ajustaments", href: "/settings" },
	{ name: "Tancar la sessió", href: "signout" },
];

export default function TopBar() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const userState = useSelector((state: RootState) => state.user);

	return (
		<>
			{/* When the mobile menu is open, add `overflow-hidden` to the `body` element to prevent double scrollbars */}
			<Popover
				as="header"
				className={({ open }) =>
					classNames(
						open ? "fixed inset-0 z-40 overflow-y-auto" : "",
						"bg-white shadow-xs lg:static lg:overflow-y-visible",
					)
				}
			>
				{({ open }) => (
					<>
						<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
							<div className="relative flex justify-between lg:gap-8 xl:grid xl:grid-cols-12">
								<div className="flex md:absolute md:inset-y-0 md:left-0 lg:static xl:col-span-2">
									<div className="flex shrink-0 items-center">
										<Link to="/">
											<img className="block h-12 w-auto" src={Logo} alt="ACNA" />
										</Link>
									</div>
								</div>
								<div className="min-w-0 flex-1 md:px-8 lg:px-0 xl:col-span-6">
									<div className="flex items-center px-6 py-4 md:mx-auto md:max-w-3xl lg:mx-0 lg:max-w-none xl:px-0">
										<div className="w-full"></div>
									</div>
								</div>
								<div className="flex items-center md:absolute md:inset-y-0 md:right-0 lg:hidden">
									{/* Mobile menu button */}
									<Popover.Button className="-mx-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-inset">
										<span className="sr-only">Open menu</span>
										{open ? (
											<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
										) : (
											<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
										)}
									</Popover.Button>
								</div>
								<div className="hidden lg:flex lg:items-center lg:justify-end xl:col-span-4">
									{/* Profile dropdown */}
									<Menu as="div" className="relative ml-5 shrink-0">
										<div>
											<Menu.Button className="flex rounded-full bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
												<span className="sr-only">Open user menu</span>
												<UserCircleIcon className="h-8 w-8 text-blue-800" />
											</Menu.Button>
										</div>
										<Transition
											as={Fragment}
											enter="transition ease-out duration-100"
											enterFrom="transform opacity-0 scale-95"
											enterTo="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leaveFrom="transform opacity-100 scale-100"
											leaveTo="transform opacity-0 scale-95"
										>
											<Menu.Items className="absolute right-0 z-10 mt-2 origin-top-right divide-y rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden">
												<div className="block px-4 py-2">
													<div className="text-sm">{userState.user.username}</div>
													<div className="font-semibold text-lg">{userState.user.email}</div>
												</div>
												<div>
													{userNavigation.map((item) => {
														if (item.href === "signout") {
															return (
																<Menu.Item key={item.name}>
																	{({ active }) => (
																		<button
																			type="button"
																			onClick={() => {
																				deleteSessionStorageFor("token");
																				dispatch(logOutUser());
																			}}
																			className={classNames(
																				active ? "bg-gray-100" : "",
																				"block w-full px-4 py-2 text-left text-gray-700 text-sm",
																			)}
																		>
																			{t(`layout.menu.${item.href.replace("/", "")}`)}
																		</button>
																	)}
																</Menu.Item>
															);
														} else {
															return (
																<Menu.Item key={item.name}>
																	{({ active }) => (
																		<a
																			href={item.href}
																			className={classNames(
																				active ? "bg-gray-100" : "",
																				"block px-4 py-2 text-gray-700 text-sm",
																			)}
																		>
																			{t(`layout.menu.${item.href.replace("/", "")}`)}
																		</a>
																	)}
																</Menu.Item>
															);
														}
													})}
												</div>
											</Menu.Items>
										</Transition>
									</Menu>
									{!location.pathname.includes("add") &&
										!location.pathname.includes("edit") &&
										!location.pathname.includes("accidents") && (
											<div>
												<Link
													to="/accidents/add"
													className="ml-6 inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
												>
													{t("layout.new_accident")}
												</Link>
											</div>
										)}
								</div>
							</div>
						</div>

						<Popover.Panel as="nav" className="lg:hidden" aria-label="Global">
							{!location.pathname.includes("add") &&
								!location.pathname.includes("edit") &&
								!location.pathname.includes("accidents") && (
									<div className="mx-auto max-w-3xl space-y-1 px-2 pt-2 pb-3 sm:px-4">
										<Link
											to="/accidents/add"
											className="ml-6 inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
										>
											{t("layout.new_accident")}
										</Link>
									</div>
								)}
							<div className="border-gray-200 border-t pt-4 pb-3">
								<div className="mx-auto flex max-w-3xl items-center px-4 sm:px-6">
									<div className="shrink-0">
										<UserCircleIcon className="h-8 w-8 text-blue-800" />
									</div>
								</div>
								<div className="mx-auto mt-3 max-w-3xl space-y-1 px-2 sm:px-4">
									{userNavigation.map((item) => {
										if (item.href === "signout") {
											return (
												<button
													key={item.name}
													type="button"
													onClick={() => {
														deleteSessionStorageFor("token");
														dispatch(logOutUser());
													}}
													className="block rounded-md px-3 py-2 font-medium text-base text-gray-500 hover:bg-gray-50 hover:text-gray-900"
												>
													{t(`layout.menu.${item.href.replace("/", "")}`)}
												</button>
											);
										} else {
											return (
												<a
													key={item.name}
													href={item.href}
													className="block rounded-md px-3 py-2 font-medium text-base text-gray-500 hover:bg-gray-50 hover:text-gray-900"
												>
													{t(`layout.menu.${item.href.replace("/", "")}`)}
												</a>
											);
										}
									})}
								</div>
							</div>
						</Popover.Panel>
					</>
				)}
			</Popover>
		</>
	);
}
